import type { PurchaseOrder } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardList, Download, ExternalLink } from "lucide-react";
import { useEffect, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import {
  createPurchaseOrderFromQuote,
  downloadPurchaseOrderPdf,
  purchaseOrderKeys,
} from "../../../lib/purchaseOrdersApi.js";
import { xopQuoteKeys } from "../../../lib/xopQuotesApi.js";
import { purchaseOrderTitle } from "./purchase-order-ui.js";

/**
 * The moment after a reseller accepts a quote (CEO, 2026-09-17).
 *
 * Accepting tells XOP Networks to place the order; most buyers' own books then
 * need a PURCHASE ORDER for it. So the page does not just close the
 * confirmation — it thanks them, says XOP has been told, and offers the next
 * step in the same breath: **Create purchase order**, with an optional field
 * for the buyer's own PO number.
 *
 * THREE STATES, one dialog:
 *   - `thanks` — straight after Accept: the thank-you, then the offer.
 *   - `create` — the same offer, opened later from an accepted quote that has
 *     no purchase order yet (no thank-you: that moment has passed).
 *   - success — "Purchase order PO-… created", with Download PDF and Open.
 *
 * crm-api builds the PO from the quote (lines, prices, customer, reseller),
 * renders the PDF and emails it to the buyer and to XOP — this dialog only
 * asks for it. The call is idempotent upstream: pressing it for a quote that
 * already has one answers with THAT purchase order, which is exactly what
 * the success state should show.
 *
 * "Not now" closes it and nothing is created. The quote page keeps offering
 * the button until a purchase order exists.
 */

export type QuotePurchaseOrderDialogMode = "thanks" | "create";

export function QuotePurchaseOrderDialog({
  open,
  mode,
  quote,
  onClose,
}: {
  open: boolean;
  mode: QuotePurchaseOrderDialogMode;
  quote: { id: number; number: string | null };
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [poNumber, setPoNumber] = useState("");
  const [created, setCreated] = useState<PurchaseOrder | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const create = useMutation({
    mutationFn: () => createPurchaseOrderFromQuote(quote.id, { poNumber }),
    onSuccess: (po) => {
      setCreated(po);
      // The quote now points at its purchase order, and the PO list has a row.
      void queryClient.invalidateQueries({ queryKey: xopQuoteKeys.detail(quote.id) });
      void queryClient.invalidateQueries({ queryKey: ["xop-quotes"] });
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    },
  });

  // A dialog that is opened again starts from its offer, not from last
  // time's success or error.
  const { reset } = create;
  useEffect(() => {
    if (open) {
      setCreated(null);
      setPoNumber("");
      setDownloadError(null);
      reset();
    }
  }, [open, reset]);

  const quoteLabel = quote.number ?? "your quote";

  const download = async (po: PurchaseOrder): Promise<void> => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadPurchaseOrderPdf(po);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "The PDF could not be downloaded.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={create.isPending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="po-dialog-title"
    >
      {created ? (
        <>
          <DialogTitle id="po-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="span" sx={{ color: "success.main", display: "flex" }}>
              <CheckCircle2 size={20} />
            </Box>
            Purchase order {purchaseOrderTitle(created)} created
          </DialogTitle>
          <DialogContent data-testid="po-dialog-success">
            <DialogContentText sx={{ fontSize: 14 }}>
              Your purchase order for {quoteLabel} is ready
              {created.poNumber ? ` under your PO number ${created.poNumber}` : ""}. A copy has been
              emailed to you and to XOP Networks.
            </DialogContentText>
            {downloadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {downloadError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button color="inherit" onClick={onClose}>
              Close
            </Button>
            {created.hasPdf && (
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                disabled={downloading}
                onClick={() => void download(created)}
              >
                Download PDF
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<ExternalLink size={16} />}
              onClick={() => {
                onClose();
                navigate(`/purchase-orders/${created.id}`);
              }}
            >
              Open purchase order
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle id="po-dialog-title">
            {mode === "thanks"
              ? `Thank you — quote ${quoteLabel} accepted`
              : `Create a purchase order for quote ${quoteLabel}`}
          </DialogTitle>
          <DialogContent data-testid={mode === "thanks" ? "po-dialog-thanks" : "po-dialog-create"}>
            {mode === "thanks" && (
              <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
                XOP Networks has been told you accepted {quoteLabel} and will be in touch about your
                order.
              </DialogContentText>
            )}
            <DialogContentText sx={{ fontSize: 14 }}>
              Need a purchase order for your records? XOP Networks can make one from this quote — the
              same items and prices — and email it to you and to XOP.
            </DialogContentText>
            <TextField
              fullWidth
              size="small"
              label="Your PO number (optional)"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              disabled={create.isPending}
              inputProps={{ maxLength: 100, "aria-label": "Your PO number" }}
              sx={{ mt: 2 }}
            />
            {create.error && (
              <Alert severity="error" sx={{ mt: 2 }} data-testid="po-dialog-error">
                {(create.error as Error).message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={onClose} disabled={create.isPending}>
              Not now
            </Button>
            <Button
              variant="contained"
              startIcon={<ClipboardList size={16} />}
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Creating…" : "Create purchase order"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
